import { css, html, type TemplateResult } from "lit";
import type { Option } from "./protocol";

// What a player's level unlocks (platform/progression) as the table draws it: the card back on their
// face-down cards, library and hand (platform/card_backs), the frame around their cards and the plate
// behind their name, each a look keyed by its id (platform/cosmetics), and the battlefield under the
// table they sit at.

export type CardBack = { id: string; name: string; creator: string; drive_id: string };
export type Style = { id: string; name: string };
export type Battlefield = { id: string; card: string; artist: string; set_name: string; source: string };

// What everyone at a game sees of a player's choices; their battlefield is theirs alone.
export type SeatLooks = { card_back: string | null; card_frame: string | null; name_plate: string | null };

export type TableCosmeticsJson = { players: { user_id: string; card_back: Option<string>; card_frame: Option<string>; name_plate: Option<string> }[] };

export function seatLooksOf(row: TableCosmeticsJson["players"][number]): SeatLooks {
  const some = (o: Option<string>) => (o.kind === "Some" ? o.values[0] : null);
  return { card_back: some(row.card_back), card_frame: some(row.card_frame), name_plate: some(row.name_plate) };
}

export function cardBackUrl(id: string): string {
  return `/art/card-back-${id}.jpg`;
}

export function battlefieldUrl(id: string): string {
  return `/art/battlefield-${id}.jpg`;
}

// Its 640px-wide preview (scripts/art-sync), for Settings' gallery and the profile.
export function battlefieldPreviewUrl(id: string): string {
  return `/art/battlefield-${id}-small.jpg`;
}

// The back a player who hasn't chosen one gets: the classic Magic back, without its logo.
export const DEFAULT_CARD_BACK = "classic-dots-hd-remake-spooky-noodle";

// A card back's style: MPC Autofill's images carry the print bleed, which `background-size` crops.
export function cardBackStyle(id: string | null | undefined): string {
  return `--card-back: url("${cardBackUrl(id || DEFAULT_CARD_BACK)}")`;
}

export function namePlateClass(id: string | null | undefined): string {
  return id ? `plate plate-${id}` : "";
}

// A card back drawn from its image, the bleed cropped away.
export const cardBackStyles = css`
  .with-back {
    background: var(--card-back) center / 110% 107.14% no-repeat, #141414 !important;
    border: 0 !important; outline: 0 !important;
  }
`;

// A frame is a ring over the card's edge: on a full card it covers the printed black border (3.6% of
// its width), so the card reads as printed in it; on a battlefield tile it sits inside the colour edge.
export const cardFrameStyles = css`
  .card[class*="look-"]::after {
    content: ""; position: absolute; inset: 0; z-index: 2; pointer-events: none; border-radius: inherit;
    padding: max(2px, calc(var(--card-w, 110px) * 0.036));
    background: var(--ring);
    -webkit-mask: linear-gradient(#000 0 0) content-box exclude, linear-gradient(#000 0 0);
    mask: linear-gradient(#000 0 0) content-box exclude, linear-gradient(#000 0 0);
    box-shadow: inset 0 0 0 1px var(--ring-edge, rgb(0 0 0 / 0.5));
  }
  .card.crop[class*="look-"]::after { padding: 2px; }
  .look-gilded {
    --ring: linear-gradient(135deg, #f6e3a1, #b8862d 28%, #f3d27a 48%, #8c6420 70%, #e9c66e 88%, #a87a28);
    --ring-edge: rgb(58 38 8 / 0.8);
  }
  .look-etched-foil {
    --ring:
      repeating-linear-gradient(45deg, rgb(255 255 255 / 0.32) 0 1px, transparent 1px 3px),
      linear-gradient(115deg, #b8c6db, #f5d0fe 18%, #c4f1f9 36%, #fef3c7 54%, #ddd6fe 72%, #a7f3d0 88%, #b8c6db);
    --ring-edge: rgb(255 255 255 / 0.5);
  }
  .look-obsidian {
    --ring: linear-gradient(160deg, #4a4058, #0d0b12 38%, #2c2436 58%, #050407 82%, #3a3046);
    --ring-edge: rgb(201 153 230 / 0.45);
  }
  .look-verdigris {
    --ring: linear-gradient(140deg, #8fcbb2, #3d7f6c 32%, #b4e0cc 52%, #2f5f52 78%, #6aa892);
    --ring-edge: rgb(22 52 44 / 0.8);
  }
  .look-stained-glass {
    --ring: conic-gradient(from 25deg, #f3e2b0 0 20%, #2a7fd0 0 40%, #4a3a52 0 60%, #d3302a 0 80%, #23935a 0);
    --ring-edge: #0b0b0b;
  }
  .look-moonsilver {
    --ring: linear-gradient(150deg, #f4f6fb, #9aa3b5 26%, #e6eaf2 46%, #7d8698 68%, #dfe4ee 88%, #8e97a8);
    --ring-edge: rgb(30 34 44 / 0.7);
  }
`;

// A plate behind a player's name, in the hue of one of the five basic lands or all five.
export const namePlateStyles = css`
  .plate {
    display: inline-block; max-width: 100%; box-sizing: border-box; padding: 1px 9px 2px; border-radius: 3px;
    border: 1px solid rgb(0 0 0 / 0.6); color: var(--plate-ink); text-shadow: var(--plate-shadow, 0 1px 1px rgb(0 0 0 / 0.55));
    background: var(--plate);
    box-shadow: inset 0 1px 0 rgb(255 255 255 / 0.28), inset 0 -1px 0 rgb(0 0 0 / 0.25), 0 1px 3px rgb(0 0 0 / 0.5);
  }
  .plate-dawnlight { --plate: linear-gradient(#f6ecce, #d6c189); --plate-ink: #2b2414; --plate-shadow: 0 1px 0 rgb(255 255 255 / 0.5); }
  .plate-tidewater { --plate: linear-gradient(#3479b8, #163e6b); --plate-ink: #eaf4ff; }
  .plate-nightshade { --plate: linear-gradient(#41344d, #17121c); --plate-ink: #dcc8f2; }
  .plate-ember { --plate: linear-gradient(#cf5233, #7a2214); --plate-ink: #fff1e6; }
  .plate-thornwood { --plate: linear-gradient(#468955, #1d4428); --plate-ink: #e8f6e3; }
  .plate-prismatic {
    --plate: linear-gradient(90deg, #cdbb86, #3479b8 26%, #41344d 50%, #b24a30 74%, #3f7d4e);
    --plate-ink: #ffffff; --plate-shadow: 0 1px 2px rgb(0 0 0 / 0.9);
  }
`;

// What a choice looks like, small: in Settings' pickers and on the profile's road.
export function cardFramePreview(id: string): TemplateResult {
  return html`<span class="frame-preview card look-${id}" aria-hidden="true"><span></span></span>`;
}

export function namePlatePreview(id: string, name: string): TemplateResult {
  return html`<span class="plate-preview"><span class=${namePlateClass(id)}>${name}</span></span>`;
}

export function cardBackPreview(id: string): TemplateResult {
  return html`<span class="back-preview with-back" style=${cardBackStyle(id)} aria-hidden="true"></span>`;
}

export function battlefieldPreview(id: string): TemplateResult {
  return html`<img class="field-preview" src=${battlefieldPreviewUrl(id)} alt="" loading="lazy" decoding="async">`;
}

export const previewStyles = css`
  .back-preview { display: block; aspect-ratio: 63 / 88; border-radius: 4.5% / 3.2%; box-shadow: 0 1px 3px rgb(0 0 0 / 0.6); }
  .frame-preview {
    position: relative; display: block; aspect-ratio: 63 / 88; border-radius: 5% / 3.6%; overflow: hidden; --card-w: 80px;
    background: radial-gradient(ellipse at 50% 30%, #4a4a52, #1e1e22 75%);
  }
  .frame-preview > span { position: absolute; left: 12%; right: 12%; top: 10%; height: 44%; border-radius: 2px; background: linear-gradient(160deg, #6c6c78, #34343c); }
  .plate-preview { display: grid; place-items: center; min-height: 44px; padding: 8px; border-radius: 5px; background: #1a1a1a; font-size: 13px; font-weight: 600; }
  .field-preview { display: block; width: 100%; aspect-ratio: 16 / 10; object-fit: cover; border-radius: 5px; background: var(--surface-3); }
`;
